import type { StateAction } from "@entrilia/shared/types";
import { sanitizeEmail } from "@entrilia/shared/utilities/emailHelper";
import {
  combineValidators,
  emailValidator,
  maxCharactersValidator,
  type Validator,
} from "@entrilia/shared/utilities/validators";
import type { Field, FieldIdToValueMap } from "../../../api/types/objectTypes";

export interface FieldEditEntry {
  field: Field;
  value: unknown;
  validationError?: string;
}

export interface FieldsEditState {
  fields: FieldEditEntry[];
  isDirty: boolean;
  isValid: boolean;
}

const isValueMissing = (value: unknown): boolean =>
  value === undefined || value === null || (typeof value === "string" && value.trim() === "");

const getTextFieldValidator = (field: Field): Validator<string> | undefined => {
  const validators: Validator<string>[] = [];

  if (field.type === "Email") {
    validators.push(emailValidator);
  }

  const maxLength = field.configuration?.$type === "Text" ? field.configuration.maxLength : undefined;
  if (maxLength !== undefined) {
    validators.push(maxCharactersValidator(maxLength));
  }

  if (validators.length === 0) {
    return undefined;
  }

  const validator = combineValidators(...validators);
  return field.type === "Email" ? (value) => validator(sanitizeEmail(value)) : validator;
};

const getFieldValidationError = (field: Field, value: unknown): string | undefined => {
  if (field.attributes.includes("Required") && isValueMissing(value)) {
    return "Value cannot be empty";
  }

  if (typeof value === "string") {
    const result = getTextFieldValidator(field)?.(value);
    if (result && !result.isValid) {
      return result.error;
    }
  }

  return undefined;
};

export const validateFields = (fields: FieldEditEntry[]): FieldEditEntry[] =>
  fields.map((entry) => ({ ...entry, validationError: getFieldValidationError(entry.field, entry.value) }));

export const getInitialFieldsEditState = (fields: Field[], values: FieldIdToValueMap): FieldsEditState => {
  const entries = validateFields(fields.map((field) => ({ field, value: values[field.id] })));

  return {
    fields: entries,
    isDirty: false,
    isValid: entries.every((entry) => !entry.validationError),
  };
};

const sanitizeFieldValue = (field: Field, value: unknown): unknown =>
  field.type === "Email" && typeof value === "string" ? sanitizeEmail(value) : value;

export const getFieldValues = (state: FieldsEditState): FieldIdToValueMap =>
  Object.fromEntries(
    state.fields.map((entry) => [entry.field.id, sanitizeFieldValue(entry.field, entry.value) ?? null])
  );

export const updateFieldValueAction =
  (fieldId: string, value: unknown): StateAction<FieldsEditState> =>
  (state) => {
    const fields = validateFields(
      state.fields.map((entry) => (entry.field.id === fieldId ? { ...entry, value } : entry))
    );

    return {
      ...state,
      fields,
      isDirty: true,
      isValid: fields.every((entry) => !entry.validationError),
    };
  };
